// FE#135 — which billing view the admin-billing page should render.
//
// Root cause of FE#135: `displaySubscription = workspaceSubscription || latestSubscription`
// fell back to the LATEST subscription (incl. expired/cancelled ones) whenever the current
// workspace had no ACTIVE subscription. For a dual-workspace user sitting on their UNPAID
// workspace, `latestSubscription` is a dead 2023 bluesnap CANCELLED relic — which then rendered
// as the "Current Subscription" with a red "Cancelled" badge, as if it were live billing.
//
// The rule: the "Current Subscription" card must only ever show a genuinely CURRENT (active)
// subscription. A workspace with no active subscription shows the no-active-subscription view —
// never a dead relic dressed up as current. Note that `activeSubscriptionSelector` already keeps
// chargebee `active` / `non_renewing` / `in_trial` subs, so a legitimately-cancelled-but-still-paid
// subscription arrives here as `hasActiveSubscription: true` and correctly keeps its billing card.

export type BillingViewMode = 'billing' | 'trial' | 'noSubscription';

export interface BillingViewModeInput {
  /** Whether the CURRENT workspace has an active subscription (activeSubscriptionSelector != null). */
  hasActiveSubscription: boolean;
  /** plan_id of the active subscription, if any. */
  activePlanId?: string | null;
  /** plan_id of the latest subscription on the workspace, INCLUDING expired/cancelled relics. */
  latestPlanId?: string | null;
}

export function selectBillingViewMode({
  hasActiveSubscription,
  activePlanId,
  latestPlanId,
}: BillingViewModeInput): BillingViewMode {
  // Trial view: an active trial, or — with no active sub — a latest trial to re-activate.
  if (activePlanId === 'trial' || (!hasActiveSubscription && latestPlanId === 'trial')) {
    return 'trial';
  }

  // "Current Subscription" card: only ever a genuinely active (current) subscription.
  if (hasActiveSubscription) {
    return 'billing';
  }

  // No active subscription on this workspace — never surface a dead/cancelled relic as current.
  return 'noSubscription';
}
